import {
  quatFromAxisAngle,
  quatMultiply,
  rotatedAabbHalfExtents,
} from '../engine/quat'
import { ObjectRuntimeState, Vec3 } from '../types'
import { playSample } from './audio'
import { FRAME_SECONDS } from './constants'
import { FAIL_GAMEOVER_SFX, GOAL_SUCCESS_SFX } from './missionSfx'
import { findStateByName, overlapsAabbByHalf } from './sharedHelpers'
import { BehaviorHandler, MissionBehaviors, HudStyle } from './types'

/**
 * RJF12（M12 PK サッカー）のミッション環境ビヘイビア。
 *
 * テンプレート rbxl（src/missions/templates/RJF12.rbxl）のギミックを移植する:
 * - ③キーパー/KeeperScript: ①ボールがキーパーに触れたらセーブ
 *   （「しっぱい・・・」表示 + Pretty Miss 音 + ボール destroy）。
 *   キーパーの左右移動は学習者のプログラム。
 * - Goal/GoalJudge/GoalJudgeScript: ①ボールがゴール枠内の判定板に触れたら
 *   ゴール（「ゴール！」表示 + Jabu's Celebration 音 + ボール destroy）。
 * - Goal/GoalJudgeMiss ×3/GoalJudgeMissScript: 枠外の判定板に触れたら
 *   セーブと同じ失敗扱い。
 *
 * プレビューでの近似・意図的な差異:
 * - ボールと判定板の接触は AABB 重なりの毎フレーム監視で判定する。
 * - 「ゴール！」「しっぱい・・・」は HUD テキストで再現する。
 * - 実機のゴール時はご褒美のスポーツカー 3 台が出現するが、車は多数パーツの
 *   Model でプレビューのクローン機構（単一パーツ）に乗らないため省略する。
 * - 効果音はゴールに成功音、セーブ・枠外（失敗）に失敗音を実音源で鳴らす。
 */

/** ボール・キーパーのオブジェクト名。 */
const BALL_NAME = '①ボール'
const KEEPER_NAME = '③キーパー'

/** ゴール判定板のオブジェクト名（GoalJudgeScript が付く板）。 */
const GOAL_JUDGE_NAME = 'GoalJudge'

/**
 * 失敗判定のオブジェクト名（GoalJudgeMissScript が付く板 3 枚）。
 * テンプレート rbxl の実測で、Goal フォルダ配下の 3 枚は GoalJudgeMiss /
 * GoalJudgeMiss1 / GoalJudgeMiss2 と相異なる名前を持ち、それぞれに
 * GoalJudgeMissScript が付く（生成済みシーン JSON でも個別名を確認済み）。
 * GoalJudge1〜4 は枠の見た目パーツでスクリプトなし（判定対象外）。
 */
const MISS_JUDGE_NAMES: ReadonlyArray<string> = [
  'GoalJudgeMiss',
  'GoalJudgeMiss1',
  'GoalJudgeMiss2',
]

/**
 * 実行時の向き（Y ヨー(rotationY) ∘ orientation）を反映した、ワールド軸平行の
 * AABB half extents（runtime の worldAabbHalfExtents と同方式）。テンプレートの
 * GoalJudgeMiss1/2 は Y 軸 ±90° 回転した側面の判定板（実体は x=±20 にある
 * z 方向 100 の縦長壁）のため、size をそのまま使うとコース中央を z=6 で
 * 横切る幅 100 の壁として誤判定し、真っ直ぐなシュートが途中で失敗扱いに
 * なってしまう。
 */
const worldAabbHalfExtents = (state: ObjectRuntimeState): Vec3 =>
  rotatedAabbHalfExtents(
    quatMultiply(quatFromAxisAngle('y', state.rotationY), state.orientation),
    state.size,
  )

/** 判定 GUI の実機デザイン（ScreenGui/TextLabel の実測値）。 */
const JUDGE_HUD_STYLE: HudStyle = {
  background: '#FFFF7F',
  color: '#000000',
  border: '#AA0000',
  borderWidth: 5,
  wide: true,
}

export const createRjf12Behaviors = (): MissionBehaviors => {
  /**
   * ボール監視ループ（Keeper / GoalJudge / GoalJudgeMiss の統合）。
   * Lua の各スクリプトの isTouch フラグ（1 回だけ発火）は、判定成立時に
   * ループ自体を return で終えることで再現する。
   */
  const watchBall: BehaviorHandler = async (ctx) => {
    const { world } = ctx
    const initial = findStateByName(world, BALL_NAME)
    if (!initial) {
      return
    }
    const ballId = initial.id
    while (!ctx.signal.aborted) {
      const ball = world.getState().get(ballId)
      if (!ball) {
        return
      }
      const ballHalf = worldAabbHalfExtents(ball)
      // キーパーのセーブ（Lua: KeeperScript）。ゴール判定より先に見る
      // （キーパーはゴール枠の手前に立つため、実機でも先に触れる）。
      const keeper = findStateByName(world, KEEPER_NAME)
      if (
        keeper &&
        overlapsAabbByHalf(
          ball.position,
          ballHalf,
          keeper.position,
          worldAabbHalfExtents(keeper),
        )
      ) {
        world.destroy(ballId)
        playSample(FAIL_GAMEOVER_SFX.url, FAIL_GAMEOVER_SFX.volume, ctx.signal)
        ctx.setHudText?.('しっぱい・・・', JUDGE_HUD_STYLE)
        return
      }
      // ゴール判定（Lua: GoalJudgeScript）。
      const goalJudge = findStateByName(world, GOAL_JUDGE_NAME)
      if (
        goalJudge &&
        overlapsAabbByHalf(
          ball.position,
          ballHalf,
          goalJudge.position,
          worldAabbHalfExtents(goalJudge),
        )
      ) {
        world.destroy(ballId)
        playSample(GOAL_SUCCESS_SFX.url, GOAL_SUCCESS_SFX.volume, ctx.signal)
        ctx.setHudText?.('ゴール！', JUDGE_HUD_STYLE)
        return
      }
      // 失敗判定（Lua: GoalJudgeMissScript ×3）。
      for (const missName of MISS_JUDGE_NAMES) {
        const judge = findStateByName(world, missName)
        if (
          judge &&
          overlapsAabbByHalf(
            ball.position,
            ballHalf,
            judge.position,
            worldAabbHalfExtents(judge),
          )
        ) {
          world.destroy(ballId)
          playSample(
            FAIL_GAMEOVER_SFX.url,
            FAIL_GAMEOVER_SFX.volume,
            ctx.signal,
          )
          ctx.setHudText?.('しっぱい・・・', JUDGE_HUD_STYLE)
          return
        }
      }
      await ctx.wait(FRAME_SECONDS)
    }
  }

  return { onPlay: [watchBall] }
}
